import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { withApiKey, withBearerJson } from '@/app/helpers/auth.context';
import { Observable, map } from 'rxjs';
import { AuthenticationService } from '../config/auth.service';
import { withBearer } from '../../../helpers/auth.context';

export interface ExchangeRateConvertRequest {
  amount: number;
  fromCurrencyId: number;
  toCurrencyId: number;
  date: string; // ISO fecha o formato aceptado por backend
}

// Ajusta la forma si el backend devuelve otra estructura
// Nueva forma detectada por ejemplo del usuario:
// {
//   "idRate": 2168,
//   "fromCurrency": { ... },
//   "toCurrency": { ... },
//   "originalAmount": 1,
//   "convertedAmount": "1.00",
//   "rate": "1",
//   "date": "2025-07-18T13:06:47.408Z"
// }
export interface ExchangeRateConvertResponseData {
  idRate?: number;
  fromCurrency?: Currency;
  toCurrency?: Currency;
  originalAmount?: number; // monto original
  convertedAmount?: string | number; // puede venir string
  rate?: string | number;
  date?: string;
  // Campos legacy (compatibilidad si backend anterior con wrapper data):
  amount?: number;
  fromCurrencyId?: number;
  toCurrencyId?: number;
}

export interface ExchangeRateConvertResponse<T = ExchangeRateConvertResponseData> {
  data: T;
  meta?: any;
}

export interface Currency {
  id: number;
  name: string;
  code: string; // ISO code e.g. USD
  symbol: string; // $, € , etc.
  eurConversionRate: number; // Factor relativo al EUR (según backend)
}

export interface CurrencyListResponse<T = Currency[]> {
  data: T;
  meta?: any;
}

@Injectable({
  providedIn: 'root',
})
export class ExchangeRateService {
  private http = inject(HttpClient);
  private authService = inject(AuthenticationService);

  /** Convierte `amount` entre monedas usando la fecha dada.
   * Tolera dos formatos de respuesta: envuelto en {data: {...}} o directo.
   */
  convert<T = ExchangeRateConvertResponseData>(body: ExchangeRateConvertRequest): Observable<T> {
    return this.http
      .post<ExchangeRateConvertResponse<T> | T>('/exchange-rate/convert', body, {
        ...withBearerJson(this.authService.getToken()),
      })
      .pipe(
        map((res: ExchangeRateConvertResponse<T> | T) => {
          return (res as ExchangeRateConvertResponse<T>).data
            ? (res as ExchangeRateConvertResponse<T>).data
            : (res as T);
        })
      );
  }

  /** Lista monedas disponibles con soporte de paginación y búsqueda */
  getCurrencies<T = Currency[]>(
    limit: number = 10,
    page: number = 1,
    orderBy: string = 'id',
    orderDirection: 'asc' | 'desc' = 'desc',
    search: string = ''
  ): Observable<T> {
    const params: Record<string, string> = {
      limit: String(limit),
      page: String(page),
      orderBy,
      orderDirection,
      search,
    };
    return this.http
      .get<CurrencyListResponse<T>>('/currency', { ...withBearerJson(this.authService.getToken()), params })
      .pipe(map((res) => res.data));
  }
}
